import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SITE } from "@/lib/site";
import { adminsExist, claimFirstAdmin } from "@/lib/admin.functions";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: `Sign in — ${SITE.name}` },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const { data: hasAdmins, refetch: refetchAdmins } = useQuery({
    queryKey: ["admins-exist"],
    queryFn: () => adminsExist(),
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(!!session);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Signed in.");
    await refetchAdmins();
    // If an admin already exists, go to admin. Otherwise stay so they can claim.
    const exists = (await adminsExist()).exists;
    if (exists) navigate({ to: "/admin" });
  }

  async function onClaim() {
    setClaiming(true);
    try {
      const res = await claimFirstAdmin();
      setClaiming(false);
      if (res.claimed) {
        toast.success("You are now the first admin.");
        navigate({ to: "/admin" });
      } else {
        toast.error("An admin already exists. Ask them to grant you access.");
        await refetchAdmins();
      }
    } catch (e: any) {
      setClaiming(false);
      toast.error(e?.message ?? "Could not claim admin");
    }
  }

  const showClaim = signedIn && hasAdmins && !hasAdmins.exists;

  return (
    <div className="min-h-screen bg-paper-soft flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-background border border-border p-8">
        <Link to="/" className="block text-center">
          <h1 className="headline-display text-3xl">{SITE.name}</h1>
          <p className="eyebrow mt-2">Editorial sign in</p>
        </Link>

        {showClaim ? (
          <div className="mt-8 space-y-4">
            <div className="border border-border bg-paper-soft p-4 text-sm">
              <p className="font-medium">No admin exists yet.</p>
              <p className="text-muted-foreground mt-1">
                You're signed in. Claim the first admin role to access the newsroom.
              </p>
            </div>
            <button
              onClick={onClaim}
              disabled={claiming}
              className="w-full bg-foreground text-background py-2.5 font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
            >
              {claiming ? "Claiming…" : "Claim first admin"}
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                setSignedIn(false);
              }}
              className="w-full border border-border py-2 text-sm hover:bg-paper-soft"
            >
              Sign out
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <div>
                <label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-foreground text-background py-2.5 font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <p className="mt-6 text-xs text-muted-foreground text-center">
              {hasAdmins && !hasAdmins.exists
                ? "First sign-in: create an account in Supabase, sign in here, then claim admin."
                : "Editor accounts only. Public sign-ups are disabled."}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
